import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { IRole } from '@core/interfaces/role.interface';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { SnackbarService } from '@core/services/snackbar.service';
import { TableStateService } from '@core/services/table-state.service';
import { UserInvitationService } from '@core/services/user-invitation.service';
import { UserService } from '@core/services/user.service';
import { IUserInvitation } from '@interfaces/user-invitation.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base.component';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { CreateEntityButtonComponent } from '@shared/components/create-entity-button/create-entity-button.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDataRefreshRequestParams,
  IDatatableConfiguration,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EventKpiCardComponent } from '@shared/components/event-kpi-card/event-kpi-card.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { getRoleIcon } from '@utilities/role-icon.util';
import { translateRoleName } from '@utilities/role-name.util';
import { roleExceedsPermissions } from '@utilities/role-permission-comparison.util';
import { isAfter } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable, tap, throwError } from 'rxjs';
import { UserManagementSharedDataStore } from '../user-management-shared-data.store';
import { CampaignSubUserInvitationComponent } from './campaign-sub-user-invitation/campaign-sub-user-invitation.component';
import { ICampaignSubUserInvitationDialogData } from './campaign-sub-user-invitation/campaign-sub-user-invitation.interface';
import { SelectRoleDialogService } from './select-role-dialog/select-role-dialog.service';
import { SelectTeamDialogService } from './select-team-dialog/select-team-dialog.service';
import { IUserManagementUserRow, TUserManagementUserStatus } from './user-management-users-tab.interface';
import { UserManagementUsersTabStore } from './user-management-users-tab.store';

@Component({
  imports: [
    CreateEntityButtonComponent,
    DatatableComponent,
    DotsSpinnerComponent,
    EventKpiCardComponent,
    HasPermissionDirective,
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    StatusBadgeComponent,
    TranslateModule,
  ],
  providers: [UserManagementUsersTabStore],
  selector: 'app-user-management-users-tab',
  styleUrl: './user-management-users-tab.component.scss',
  templateUrl: './user-management-users-tab.component.html',
})
export class UserManagementUsersTabComponent extends BaseComponent {
  public readonly store = inject(UserManagementUsersTabStore);
  public readonly sharedData = inject(UserManagementSharedDataStore);

  /** Ids of pending/expired invitations with a Resend request currently in flight - per-invitation
   * rather than a single shared "any action anywhere" flag, so resending one row's invitation
   * never disables, or gets confused with the result of, a different row's own Resend/Delete.
   * (Previously both actions shared one `invitationIdBeforeChange` signal plus a single
   * ComponentStore `switchMap` effect on this component's own store instance, so two different
   * rows' actions in flight at the same time would silently cancel or cross-talk with each
   * other.) */
  private readonly resendingInvitationIds = signal<ReadonlySet<string>>(new Set<string>());

  /** Ids of pending/expired invitations with a Delete (revoke) request currently in flight - same
   * per-invitation reasoning as resendingInvitationIds above. */
  private readonly revokingInvitationIds = signal<ReadonlySet<string>>(new Set<string>());

  private readonly activeRoleFilter = signal<string[]>([]);
  private readonly activeTeamFilter = signal<string[]>([]);
  private readonly activeStatusFilter = signal<string[]>([]);
  /** Used to keep every table sorted by name ascending by default (applied directly to the
   * underlying data, not just as an initial sort state the table's own sort UI could override) -
   * `sensitivity: 'base'` gives a locale-aware, case-insensitive compare. */
  private readonly nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

  /** The currently logged-in user's own permissions - not any row's assigned role. Reactive so a
   * permission change elsewhere in the session is reflected immediately - see
   * AddCustomRoleDialogComponent's identical field for the same reasoning. */
  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  public readonly roleById = computed(
    () => new Map<string, IRole>(this.sharedData.roles$()?.map((role) => [role.id, role]) ?? []),
  );
  public readonly roleNameById = computed(
    () => new Map<string, string>(this.sharedData.roles$()?.map((role) => [role.id, this.getRoleName(role)]) ?? []),
  );
  public readonly teamNameById = computed(
    () => new Map<string, string>(this.sharedData.teams$()?.map((team) => [team.id, team.name]) ?? []),
  );
  public readonly teamIconIdById = computed(
    () => new Map<string, string | null>(this.sharedData.teams$()?.map((team) => [team.id, team.iconId ?? null]) ?? []),
  );
  /** Roles/teams start out `null` in the shared service and only become an array once the fetch
   * settles (success or failure) - used to keep the Invite User button in its skeleton form until
   * both are ready, since the invite dialog is seeded with this same data instead of fetching it
   * itself. */
  public readonly isInviteButtonLoading = computed(
    () => this.sharedData.roles$() === null || this.sharedData.teams$() === null,
  );

  /**
   * Every row of the Users table, built by merging two sources rather than reading one:
   *
   * - A 'joined' row for every entry in `sharedData.users$()` (every real member of the group,
   *   from `GET /users`) - this is the authoritative source for joined members, not the
   *   invitations list below, because a member can have joined through an invitation type this
   *   tab never fetches (e.g. a creator invitation) or through no invitation at all (e.g. the
   *   group's own creator account) and would otherwise never get a row. Role/team live on
   *   `ValidUserGroup` (the per user-group membership row), not on `IUser` directly - a login can
   *   be a member of more than one group with a different role/team in each - so the role is read
   *   off `IUser.roles`: `GET /users` is auto-scoped to the caller's own group, so the membership
   *   role is guaranteed to be the *last* entry (the backend appends the user's global role first,
   *   then the active membership's role, deduped - see `to_pydantic` in
   *   campaign-management-api). `teamId` comes directly off `IUser.teamId`.
   * - A 'pending'/'expired' row for every campaign-sub-user invitation that has NOT been accepted
   *   yet (`createdUserId` unset) - an already-accepted one is only used to fill in the matching
   *   joined row's originating invitation id/email above, never to build a second row, so a
   *   joined member is never duplicated.
   */
  public readonly allRows = computed<IUserManagementUserRow[] | null>(() => {
    const invitations = this.store.userInvitations$();
    const users = this.sharedData.users$();

    if (!invitations || !users) {
      return null;
    }

    const roleNameById = this.roleNameById();
    const teamNameById = this.teamNameById();
    const teamIconIdById = this.teamIconIdById();

    // Accepted campaign-sub-user invitations, keyed by the user id they created - used only to
    // enrich a joined row with its originating invitation id/email when one is on record.
    const acceptedInvitationByUserId = new Map<string, IUserInvitation>();

    for (const invitation of invitations) {
      if (invitation.createdUserId) {
        acceptedInvitationByUserId.set(invitation.createdUserId, invitation);
      }
    }

    const joinedRows = users.map((user): IUserManagementUserRow => {
      const invitation = acceptedInvitationByUserId.get(user.id) ?? null;
      const membershipRoleId = user.roles.length
        ? user.roles[user.roles.length - 1].id
        : (invitation?.targetRole ?? '');
      const teamId = user.teamId ?? null;

      return {
        createdUserId: user.id,
        email: invitation?.email ?? null,
        icon: user.iconId ?? null,
        id: user.id,
        invitationId: invitation?.id ?? null,
        name: user.name ?? null,
        roleId: membershipRoleId,
        roleName: roleNameById.get(membershipRoleId) ?? membershipRoleId,
        status: 'joined',
        teamIconId: teamId ? (teamIconIdById.get(teamId) ?? null) : null,
        teamId,
        teamName: teamId ? (teamNameById.get(teamId) ?? null) : null,
      };
    });

    const pendingAndExpiredRows = invitations
      .filter((invitation) => !invitation.createdUserId)
      .map((invitation): IUserManagementUserRow => {
        const isExpired = isAfter(new Date(), this.dateUtils.parseDateTimeFromApi(invitation.expire));
        const status: TUserManagementUserStatus = isExpired ? 'expired' : 'pending';
        const teamId = invitation.teamId ?? null;

        return {
          createdUserId: null,
          email: invitation.email,
          icon: null,
          id: invitation.id,
          invitationId: invitation.id,
          name: null,
          roleId: invitation.targetRole,
          roleName: roleNameById.get(invitation.targetRole) ?? invitation.targetRole,
          status,
          teamIconId: teamId ? (teamIconIdById.get(teamId) ?? null) : null,
          teamId,
          teamName: teamId ? (teamNameById.get(teamId) ?? null) : null,
        };
      });

    return [...joinedRows, ...pendingAndExpiredRows];
  });

  public readonly filteredRows = computed<IUserManagementUserRow[]>(() => {
    const rows = this.allRows() ?? [];
    const roleFilter = this.activeRoleFilter();
    const teamFilter = this.activeTeamFilter();
    const statusFilter = this.activeStatusFilter();

    return rows
      .filter((row) => {
        if (roleFilter.length && !roleFilter.includes(row.roleId)) {
          return false;
        }

        if (teamFilter.length && !(row.teamId && teamFilter.includes(row.teamId))) {
          return false;
        }

        if (statusFilter.length && !statusFilter.includes(row.status)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => this.nameCollator.compare(a.name ?? a.email ?? '', b.name ?? b.email ?? ''));
  });

  public readonly seatsUsed = computed<number | null>(() => this.store.userGroup$()?.currentSeats ?? null);
  public readonly seatsMax = computed<number | null>(() => this.store.userGroup$()?.effectiveMaxSeats ?? null);
  public readonly seatsLoading = computed<boolean>(() => this.store.userGroup$() === null);

  public readonly pendingInvitesCount = computed<number | null>(() => {
    const rows = this.allRows();

    return rows ? rows.filter((row) => row.status === 'pending').length : null;
  });
  public readonly pendingInvitesLoading = computed<boolean>(() => this.allRows() === null);

  /** How many real ('joined') members currently hold the built-in Admin role - used by isLastAdmin
   * to block reassigning away the group's one remaining Admin. A 'pending'/'expired' invitation
   * targeting the Admin role doesn't count - nobody has actually joined as Admin through it yet,
   * so changing/revoking it doesn't reduce the real Admin headcount. */
  public readonly adminUserCount = computed<number>(
    () =>
      (this.allRows() ?? []).filter((row) => row.status === 'joined' && row.roleId === (this.ERoles.campaign as string))
        .length,
  );

  public datatableColumns: TDatatableColumn<IUserManagementUserRow, string>[] = [
    {
      dataViewStrategy: (row: IUserManagementUserRow) => `${row.name ?? ''} ${row.email ?? ''}`.trim(),
      id: 'name',
      isClientSideSearchable: true,
      isSortable: true,
    },
    { id: 'roleName', isSortable: true },
    { dataViewStrategy: () => '', id: 'teamName' },
    { id: 'status', isSortable: true },
    { dataViewStrategy: () => '', id: 'actions' },
  ];

  public datatableConfig: IDatatableConfiguration<IUserManagementUserRow> = {};

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '4px',
        circleSize: '40px',
        className: 'w-[40%]',
        columnPadding: '0 8px',
        hasText: true,
        height: '32px',
        rowPadding: '8px 0',
        textWidth: '140px',
        type: 'icon',
      },
      { borderRadius: '100vw', className: 'w-[18%]', height: '28px', textWidth: '80px', type: 'text' },
      { borderRadius: '100vw', className: 'w-[18%]', height: '28px', textWidth: '80px', type: 'text' },
      { borderRadius: '100vw', className: 'w-[16%]', height: '22px', textWidth: '64px', type: 'text' },
      { borderRadius: '100vw', circleSize: '24px', className: 'w-[8%] flex justify-start', type: 'icon', width: '0' },
    ],
    filterContainerClass: 'flex items-center gap-5',
    filters: [{ inputClass: '!min-w-[220px]' }, { inputClass: '!min-w-[220px]' }, { inputClass: '!min-w-[220px]' }],
    rows: 5,
    showFilters: true,
    showHeaderControls: true,
    showPaginator: false,
    showSearchField: true,
  };

  private readonly dialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly userService = inject(UserService);
  private readonly userInvitationService = inject(UserInvitationService);
  private readonly tableStateService = inject(TableStateService);
  private readonly selectRoleDialogService = inject(SelectRoleDialogService);
  private readonly selectTeamDialogService = inject(SelectTeamDialogService);
  private readonly tableStorageKey = this.tableStateService.buildKey('user-management', 'users-tab');

  constructor() {
    super();

    this.store.ensureUserGroupLoaded();
    this.store.ensureInvitationsLoaded();

    effect(() => {
      this.sharedData.roles$();
      this.sharedData.teams$();

      this.setDatatableConfig();
    });
  }

  public onDataRefresh(params: IDataRefreshRequestParams): void {
    const filters = params.filters ?? {};

    this.activeRoleFilter.set((filters['roleId'] as string[] | undefined) ?? []);
    this.activeTeamFilter.set((filters['teamId'] as string[] | undefined) ?? []);
    this.activeStatusFilter.set((filters['status'] as string[] | undefined) ?? []);

    // Guarded no-ops once already loaded - only double as a retry for a failed initial fetch
    // (see UserManagementUsersTabStore.ensureInvitationsLoaded/ensureUserGroupLoaded's doc
    // comments).
    this.store.ensureInvitationsLoaded();
    this.store.ensureUserGroupLoaded();
  }

  public openInviteDialog(): void {
    const dialogRef = this.dialog.open<
      CampaignSubUserInvitationComponent,
      ICampaignSubUserInvitationDialogData,
      IUserInvitation
    >(CampaignSubUserInvitationComponent, {
      data: {
        roles: this.sharedData.roles$() ?? [],
        teams: this.sharedData.teams$() ?? [],
      } as ICampaignSubUserInvitationDialogData,
      disableClose: true,
      width: '700px',
    });

    dialogRef.afterClosed().subscribe((result?: IUserInvitation) => {
      if (result) {
        this.store.patchState({
          userGroup: this.nextUserGroupWithSeatsDelta(1),
          userInvitations: [...(this.store.userInvitations$() ?? []), result],
        });
        // Keeps UserManagementSharedDataStore's own independent invitations copy in sync too -
        // see that store's ensureUserInvitationsLoaded doc comment for why it needs telling
        // separately rather than sharing this store's copy.
        this.sharedData.upsertUserInvitation(result);
      }
    });
  }

  /** True only for the exact row whose "Resend Invitation" is currently in flight - drives the
   * status badge's dimmed+spinner treatment and the Resend Invitation menu item's own spinner (see
   * the template). */
  public isResendingInvitation(row: IUserManagementUserRow): boolean {
    return !!row.invitationId && this.resendingInvitationIds().has(row.invitationId);
  }

  /** True while EITHER of this row's own invitation actions (Resend or Delete) is in flight -
   * used to disable both the Resend and Delete menu items for the specific row currently being
   * acted on, without touching any other row's controls (see resendingInvitationIds/
   * revokingInvitationIds' doc comments). */
  public isInvitationActionInFlight(row: IUserManagementUserRow): boolean {
    return (
      !!row.invitationId &&
      (this.resendingInvitationIds().has(row.invitationId) || this.revokingInvitationIds().has(row.invitationId))
    );
  }

  /** Unlike Change Role/Change Team, resending is deliberately still allowed for an expired
   * invitation - that's the whole point of resending: giving a dead invite a fresh expiry.
   *
   * Calls `UserInvitationService` directly and subscribes per-row, rather than going through this
   * store's own `sendInvitation` effect (which `CampaignSubUserInvitationComponent`'s separate
   * "Invite User" dialog still uses, safely, on its own isolated per-dialog store instance) - see
   * resendingInvitationIds' doc comment for why a single shared effect on this table's one store
   * instance was the actual bug. */
  public resendInvitation(row: IUserManagementUserRow): void {
    const userInvitation = this.store.userInvitations$()?.find((ui) => ui.id === row.invitationId);

    if (!userInvitation || this.isInvitationActionInFlight(row)) {
      return;
    }

    const invitationId = userInvitation.id;

    this.resendingInvitationIds.update((ids) => new Set(ids).add(invitationId));

    this.userInvitationService
      .sendCampaignSubUserInvitation(
        userInvitation.email,
        userInvitation.message,
        userInvitation.targetRole,
        userInvitation.teamId,
      )
      .subscribe({
        error: () => {
          this.resendingInvitationIds.update((ids) => this.withoutId(ids, invitationId));
          this.snackbarService.openError(
            this.translate.instant('pages.settings.userManagement.users.resendInvitationError'),
          );
        },
        next: (newInvitation) => {
          this.resendingInvitationIds.update((ids) => this.withoutId(ids, invitationId));
          this.store.patchState({
            userInvitations: (this.store.userInvitations$() ?? []).map((ui) =>
              ui.id === invitationId ? { ...ui, expire: newInvitation.expire, id: newInvitation.id } : ui,
            ),
          });
          // Resending swaps the invitation's id (see newInvitation.id above), so the shared copy
          // can't be upserted in place by the old id - drop it and add the new one instead.
          this.sharedData.removeUserInvitation(invitationId);
          this.sharedData.upsertUserInvitation(newInvitation);
          this.snackbarService.openSuccess(
            this.translate.instant('pages.settings.userManagement.users.invitationSent'),
          );
        },
      });
  }

  public getRoleName(role: IRole): string {
    return translateRoleName(role, this.translate);
  }

  public getRoleIconForRoleId(roleId: string): string {
    const role = this.roleById().get(roleId);

    return role ? getRoleIcon(role) : '/icons/user.svg';
  }

  /** A user can only manage another user's role if (a) they hold the base permission to change
   * roles at all for a row in this status - `userChangeRoleOwn` for a joined member,
   * `campaignSubUserInvitationCreate` for a pending invitation, mirroring the "Change role" menu
   * item's own `*hasPermission` gate (which the role pill button doesn't get for free, since it's
   * a plain button rather than a `*hasPermission`-gated menu item - this check is what closes that
   * gap), and (b) that user's CURRENT role doesn't already exceed their own permissions -
   * otherwise this dialog would let them reassign someone whose existing access they can't even
   * fully see/evaluate themselves. Returns false (blocks opening the dialog) when the row's role
   * can't be found at all, as a safe default. */
  public canManageUserRole(row: IUserManagementUserRow): boolean {
    const role = this.roleById().get(row.roleId);
    const requiredPermission =
      row.status === 'joined' ? this.EPermissions.userChangeRoleOwn : this.EPermissions.campaignSubUserInvitationCreate;

    return (
      !!role &&
      this.permissionService.hasPermission(requiredPermission) &&
      !roleExceedsPermissions(role, this.currentUserPermissions())
    );
  }

  /** True only for the group's one remaining 'joined' Admin - reassigning them away from Admin
   * would leave the group with none. Never true for a 'pending'/'expired' invitation, even one
   * targeting Admin - see adminUserCount's doc comment. */
  public isLastAdmin(row: IUserManagementUserRow): boolean {
    return row.status === 'joined' && row.roleId === (this.ERoles.campaign as string) && this.adminUserCount() <= 1;
  }

  /** An expired invitation is a dead row - nothing about it (its target role/team) can be edited
   * any more. Takes priority over every other reason a role/team control might be disabled (see
   * getRoleChangeBlockedMessage), since it's true regardless of who's looking at it. */
  public getExpiredInvitationMessage(row: IUserManagementUserRow): string | null {
    return row.status === 'expired'
      ? (this.translate.instant('pages.settings.userManagement.users.expiredInvitationMessage') as string)
      : null;
  }

  /** Single source of truth for whether Select Role should be blocked for `row`, and why -
   * used to disable both the role pill button and the menu's "Change role" item with a matching
   * tooltip, and to guard onChangeRoleClick itself so the dialog can never open for a blocked row
   * regardless of which control (if any) triggered it. Checked in priority order: an expired
   * invitation first (getExpiredInvitationMessage), then canManageUserRole, then isLastAdmin. */
  public getRoleChangeBlockedMessage(row: IUserManagementUserRow): string | null {
    const expiredMessage = this.getExpiredInvitationMessage(row);

    if (expiredMessage) {
      return expiredMessage;
    }

    if (!this.canManageUserRole(row)) {
      return this.translate.instant('pages.settings.userManagement.users.cannotManageUserRole') as string;
    }

    if (this.isLastAdmin(row)) {
      return this.translate.instant('pages.settings.userManagement.users.lastAdminCannotChange') as string;
    }

    return null;
  }

  /** Change Role/Change Team are both available for 'joined' and 'pending' rows - 'expired' rows
   * still show the controls (disabled, via getRoleChangeBlockedMessage/getExpiredInvitationMessage
   * above) rather than hiding them, so the reason is visible as a tooltip. The actual save request
   * (and keeping the shared roles/teams/users data in sync) is now owned by the dialog itself via
   * `onSave` - see saveRoleChange/saveTeamChange below and ISelectRoleDialogData/
   * ISelectTeamDialogData's doc comments - so this only needs to seed the dialog and, once it
   * resolves, sync whatever roles/teams list it hands back (which may include one created via
   * "Add custom role"/"Add team" even if the flow was ultimately cancelled). */
  public onChangeRoleClick(row: IUserManagementUserRow): void {
    if (this.getRoleChangeBlockedMessage(row)) {
      return;
    }

    this.selectRoleDialogService
      .open(this.sharedData.roles$() ?? [], row.roleId, (roleId) => this.saveRoleChange(row, roleId))
      .subscribe(({ roles }) => {
        this.sharedData.setRoles(roles);
      });
  }

  /** Single source of truth for whether Select Team should be blocked for `row`, and why - used
   * to disable both the team pill button and the menu's "Change team" item with a matching
   * tooltip, and to guard onChangeTeamClick itself so the dialog can never open for a blocked row
   * regardless of which control (if any) triggered it. Mirrors getRoleChangeBlockedMessage's
   * permission check (userChangeTeamOwn for a joined member, campaignSubUserInvitationCreate for
   * a pending invitation) - unlike roles, teams have no hierarchy/last-admin concept, so expired
   * status and this base permission are the only two reasons. */
  public getTeamChangeBlockedMessage(row: IUserManagementUserRow): string | null {
    const expiredMessage = this.getExpiredInvitationMessage(row);

    if (expiredMessage) {
      return expiredMessage;
    }

    const requiredPermission =
      row.status === 'joined' ? this.EPermissions.userChangeTeamOwn : this.EPermissions.campaignSubUserInvitationCreate;

    if (!this.permissionService.hasPermission(requiredPermission)) {
      return this.translate.instant('pages.settings.userManagement.users.cannotManageUserTeam') as string;
    }

    return null;
  }

  public onChangeTeamClick(row: IUserManagementUserRow): void {
    if (this.getTeamChangeBlockedMessage(row)) {
      return;
    }

    this.selectTeamDialogService
      .open(this.sharedData.teams$() ?? [], row.teamId, (teamId) => this.saveTeamChange(row, teamId))
      .subscribe(({ teams }) => {
        this.sharedData.setTeams(teams);
      });
  }

  /** Shown as a tooltip on the Delete menu item for a 'joined' row, and used to guard
   * onDeleteClick - deleting the group's one remaining Admin would leave it with none, so it's
   * blocked the same way reassigning them away from Admin already is via isLastAdmin (see that
   * method's doc comment for why a pending/expired invitation targeting Admin never counts). */
  public getDeleteUserBlockedMessage(row: IUserManagementUserRow): string | null {
    return this.isLastAdmin(row)
      ? (this.translate.instant('pages.settings.userManagement.users.lastAdminCannotDelete') as string)
      : null;
  }

  public onDeleteClick(row: IUserManagementUserRow): void {
    if (this.getDeleteUserBlockedMessage(row)) {
      return;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.form.delete'),
        message: this.translate.instant('pages.settings.userManagement.users.deleteUserMessage', {
          name: row.name ?? row.email ?? row.id,
        }),
        title: this.translate.instant('shared.form.delete'),
      } as IConfirmDialogData,
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((confirmed?: boolean) => {
      if (!confirmed) {
        return;
      }

      if (row.status === 'joined' && row.createdUserId) {
        this.deleteJoinedUser(row.createdUserId, row.invitationId);

        return;
      }

      if (!row.invitationId) {
        return;
      }

      this.revokeInvitation(row.invitationId);
    });
  }

  /** Revokes a still-pending/expired invitation directly via UserInvitationService (see
   * resendInvitation's doc comment for why this bypasses the store's own effect). Deliberately
   * NOT optimistic like the user/role/team deletes below: those remove a row whose identity
   * (the user/role/team) survives independently of the delete request, so there's something to
   * restore on failure; an invitation row's identity IS the pending request itself, so there's
   * nothing to speculatively remove ahead of knowing the request succeeded. On failure, shows an
   * error and leaves the row in place untouched. */
  private revokeInvitation(invitationId: string): void {
    if (this.revokingInvitationIds().has(invitationId) || this.resendingInvitationIds().has(invitationId)) {
      return;
    }

    this.revokingInvitationIds.update((ids) => new Set(ids).add(invitationId));

    this.userInvitationService.revokeCampaignSubUserInvitation(invitationId).subscribe({
      error: () => {
        this.revokingInvitationIds.update((ids) => this.withoutId(ids, invitationId));
        this.snackbarService.openError(
          this.translate.instant('pages.settings.userManagement.users.revokeInvitationError'),
        );
      },
      next: () => {
        this.revokingInvitationIds.update((ids) => this.withoutId(ids, invitationId));
        this.store.patchState({
          userGroup: this.nextUserGroupWithSeatsDelta(-1),
          userInvitations: (this.store.userInvitations$() ?? []).filter((ui) => ui.id !== invitationId),
        });
        this.sharedData.removeUserInvitation(invitationId);
        this.snackbarService.openSuccess(
          this.translate.instant('pages.settings.userManagement.users.invitationRevoked'),
        );
      },
    });
  }

  private withoutId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
    const next = new Set(ids);

    next.delete(id);

    return next;
  }

  /** Computes the next `userGroup` value with `currentSeats` adjusted by `delta` (clamped at 0),
   * or the current value unchanged if it hasn't loaded yet - spreading a `null` userGroup would
   * otherwise write a broken partial `IUserGroup` (missing `id` and every other field) into
   * state. `ensureUserGroupLoaded()` fetches from this component's constructor, but nothing
   * actually gates the Invite/Resend/Delete controls on it resolving first, so every caller that
   * adjusts the seat count guards against that race with this rather than trusting it can't
   * happen. */
  private nextUserGroupWithSeatsDelta(delta: number): IUserGroup | null {
    const userGroup = this.store.userGroup$();

    return userGroup ? { ...userGroup, currentSeats: Math.max(0, userGroup.currentSeats + delta) } : userGroup;
  }

  /** For a 'joined' row, mutates the real membership directly and syncs the returned user into
   * the shared data service (so the Roles & Teams tab's counts/avatars update reactively). For a
   * 'pending' row there is no membership yet, so this goes through the dedicated
   * `PUT /invitations/campaign-sub-user/{id}` endpoint instead, which requires role_id and
   * accepts team_id together - so it preserves the row's current team. */
  private saveRoleChange(row: IUserManagementUserRow, roleId: string): Observable<unknown> {
    if (row.status === 'joined' && row.createdUserId) {
      return this.updateJoinedUserRole(row.createdUserId, roleId);
    }

    if (!row.invitationId) {
      return throwError(() => new Error('Missing invitation id'));
    }

    return this.userInvitationService
      .updatePendingCampaignSubUserInvitation(row.invitationId, roleId, row.teamId)
      .pipe(tap((updatedInvitation) => this.patchPendingInvitation(updatedInvitation)));
  }

  /** Same reasoning as saveRoleChange above, for teams - `teamId: null` clears the assignment via
   * the dedicated clear endpoint instead of a "set" call. */
  private saveTeamChange(row: IUserManagementUserRow, teamId: string | null): Observable<unknown> {
    if (row.status === 'joined' && row.createdUserId) {
      return this.updateJoinedUserTeam(row.createdUserId, teamId);
    }

    if (!row.invitationId) {
      return throwError(() => new Error('Missing invitation id'));
    }

    return this.userInvitationService
      .updatePendingCampaignSubUserInvitation(row.invitationId, row.roleId, teamId)
      .pipe(tap((updatedInvitation) => this.patchPendingInvitation(updatedInvitation)));
  }

  private updateJoinedUserRole(userId: string, roleId: string): Observable<unknown> {
    const userGroupId = this.store.userGroup$()?.id;

    if (!userGroupId) {
      return throwError(() => new Error('Missing user group id'));
    }

    return this.userService
      .setUserGroupRole(userId, userGroupId, roleId)
      .pipe(tap((updatedUser) => this.sharedData.upsertUser(updatedUser)));
  }

  private updateJoinedUserTeam(userId: string, teamId: string | null): Observable<unknown> {
    const userGroupId = this.store.userGroup$()?.id;

    if (!userGroupId) {
      return throwError(() => new Error('Missing user group id'));
    }

    const request$ =
      teamId === null
        ? this.userService.clearUserGroupTeam(userId, userGroupId)
        : this.userService.setUserGroupTeam(userId, userGroupId, teamId);

    return request$.pipe(tap((updatedUser) => this.sharedData.upsertUser(updatedUser)));
  }

  private patchPendingInvitation(updatedInvitation: IUserInvitation): void {
    this.store.patchState({
      userInvitations: (this.store.userInvitations$() ?? []).map((invitation) =>
        invitation.id === updatedInvitation.id ? updatedInvitation : invitation,
      ),
    });
    this.sharedData.upsertUserInvitation(updatedInvitation);
  }

  /** Removes the user from this group's membership only (`DELETE
   * /user-groups/{id}/members/{user_id}`) rather than deleting the account outright - deleting a
   * 'joined' row here should never risk touching a shared/multi-company account, or an
   * admin/support account's global identity, the way the account-level delete endpoint now can.
   *
   * Optimistic: the row disappears from the shared users list the instant the confirm dialog
   * resolves, rather than waiting on the request. If the request then fails there's no per-row
   * data left to roll back to (the row is already gone from every consumer of
   * `sharedData.users$()`), so the only correct recovery is reloading both users and invitations
   * from the server - a row's "joined" status is derived from the merge of the two, so either one
   * being stale could resurrect (or fail to resurrect) the wrong row. */
  private deleteJoinedUser(userId: string, invitationId: string | null): void {
    const userGroupId = this.store.userGroup$()?.id;

    if (!userGroupId) {
      this.showDeleteUserError();

      return;
    }

    this.sharedData.removeUser(userId);
    this.sharedData.markUserDeletePending(userId);

    this.userService.removeUserGroupMember(userGroupId, userId).subscribe({
      error: () => {
        this.showDeleteUserError();
        // Unmark before reloading - this user genuinely wasn't removed, so it's fine (expected,
        // even) for this reload to bring them back. See
        // UserManagementSharedDataStore.markUserDeletePending's doc comment for why this exists:
        // without it, a *different* row's concurrently in-flight delete could be resurrected by
        // this reload before its own request has settled.
        this.sharedData.clearUserDeletePending(userId);
        this.sharedData.reloadUsers();
        this.store.fetchInvitations();
        // Same reasoning as the two reloads above - this store's own optimistic
        // removeUserInvitation call below never ran (the request failed before reaching it), but
        // a *different* row's delete could have changed things in the meantime, so a full reload
        // is the only correct recovery here too.
        this.sharedData.reloadUserInvitations();
      },
      next: () => {
        this.sharedData.clearUserDeletePending(userId);
        this.store.patchState({
          userGroup: this.nextUserGroupWithSeatsDelta(-1),
          userInvitations: (this.store.userInvitations$() ?? []).filter((ui) => ui.id !== invitationId),
        });

        if (invitationId) {
          this.sharedData.removeUserInvitation(invitationId);
        }
      },
    });
  }

  private showDeleteUserError(): void {
    this.snackbarService.openError(this.translate.instant('pages.settings.userManagement.users.deleteUserError'));
  }

  private setDatatableConfig(): void {
    this.datatableConfig = {
      filters: [
        {
          key: 'roleId',
          label: this.translate.instant('field.role'),
          multiple: true,
          options: (this.sharedData.roles$() ?? []).map((role) => ({ label: this.getRoleName(role), value: role.id })),
          placeholder: this.translate.instant('shared.all'),
        },
        {
          key: 'teamId',
          label: this.translate.instant('field.team'),
          multiple: true,
          options: (this.sharedData.teams$() ?? []).map((team) => ({ label: team.name, value: team.id })),
          placeholder: this.translate.instant('shared.all'),
        },
        {
          key: 'status',
          label: this.translate.instant('shared.status'),
          multiple: true,
          options: (['joined', 'pending', 'expired'] as const).map((status) => ({
            label: this.translate.instant(`shared.statuses.${status}`),
            value: status,
          })),
          placeholder: this.translate.instant('shared.all'),
        },
      ],
      isClientSide: true,
      isPaginationEnabled: false,
      isRowClickEnabled: false,
      rowColor: 'var(--shikenso-gray-base)',
      storageKey: this.tableStorageKey,
    } satisfies IDatatableConfiguration<IUserManagementUserRow>;
  }
}
